import { mockStudents } from "../data/mockStudents";
import { INCLUDE_DEMO_STUDENTS, USE_LOCAL_FALLBACK } from "../config/firebaseConfig";
import { db, storage } from "../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, getDocs, getDoc, doc, setDoc, updateDoc, deleteDoc, query, where, addDoc } from "firebase/firestore";
import { emitFirestoreError } from "../lib/errorBus";

function getCachedStudents() {
  let stored = [];
  try {
    const raw = localStorage.getItem("students");
    stored = raw ? JSON.parse(raw) : [];
  } catch {
    stored = [];
  }
  let cachedFs = [];
  try {
    const raw = localStorage.getItem("students_fs_cache");
    cachedFs = raw ? JSON.parse(raw) : [];
  } catch {
    cachedFs = [];
  }
  const map = {};
  const pushUnique = (arr) => {
    (arr || []).forEach(s => {
      if (!s || !s.id) return;
      map[String(s.id)] = s;
    });
  };
  if (INCLUDE_DEMO_STUDENTS) pushUnique(mockStudents);
  pushUnique(stored);
  pushUnique(cachedFs);
  return Object.values(map);
}

export function getAllStudents() {
  return getCachedStudents();
}

export function getStoredStudents() {
  try {
    const raw = localStorage.getItem("students");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveStoredStudents(list) {
  localStorage.setItem("students", JSON.stringify(list || []));
}

export function generateStudentId(campus) {
  const code = String(campus || "gen").toLowerCase();
  const seqKey = `student_seq_${code}`;
  const seq = Number(localStorage.getItem(seqKey) || "0") + 1;
  localStorage.setItem(seqKey, String(seq));
  return `S-${code.toUpperCase()}-${String(seq).padStart(4, "0")}`;
}

export async function generateAvailableStudentId(campus) {
  const code = String(campus || "gen").toLowerCase();
  for (let i = 0; i < 200; i++) {
    const candidate = generateStudentId(code);
    if (USE_LOCAL_FALLBACK) {
      const existsLocal = getCachedStudents().some(s => String(s.id) === String(candidate));
      if (!existsLocal) return candidate;
      continue;
    }
    try {
      const ref = doc(db, "students", String(candidate));
      const snap = await getDoc(ref);
      if (!snap.exists()) return candidate;
    } catch {
      return candidate;
    }
  }
  throw new Error("Failed to generate a unique Student ID. Please try again.");
}

export function addStudent(record) {
  if (USE_LOCAL_FALLBACK) {
    const list = getStoredStudents();
    const idStr = String(record?.id || "");
    if (!idStr.trim()) {
      const err = new Error("Student ID is required.");
      err.code = "invalid-argument";
      throw err;
    }
    if (list.some(s => String(s.id) === idStr)) {
      const err = new Error("Student ID already exists.");
      err.code = "already-exists";
      throw err;
    }
    list.push(record);
    saveStoredStudents(list);
    return record;
  }
  const idStr = String(record?.id || "").trim();
  if (!idStr) {
    const err = new Error("Student ID is required.");
    err.code = "invalid-argument";
    return Promise.reject(err);
  }
  const ref = doc(db, "students", idStr);
  return getDoc(ref).then((snap) => {
    if (snap.exists()) {
      const err = new Error("Student ID already exists.");
      err.code = "already-exists";
      throw err;
    }
    return setDoc(ref, record, { merge: false });
  }).then(() => {
    const cache = getCachedStudents().filter(s => String(s.id) !== idStr).concat(record);
    localStorage.setItem("students_fs_cache", JSON.stringify(cache));
    const by = record.updatedBy || record.createdBy || "system";
    const at = new Date().toISOString();
    addDoc(collection(db, "audit_log"), { at, action: "create", studentId: idStr, by, campus: record.campus || "" }).catch(() => {});
    return record;
  }).catch((err) => {
    emitFirestoreError({
      code: err?.code,
      message: err?.message,
      operation: "Add student",
      retry: () => setDoc(ref, record, { merge: false })
    });
    throw err;
  });
}

export function updateStudent(id, patch) {
  if (USE_LOCAL_FALLBACK) {
    const list = getStoredStudents();
    const idx = list.findIndex(s => String(s.id) === String(id));
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...patch };
      saveStoredStudents(list);
      return list[idx];
    }
    return null;
  }
  const ref = doc(db, "students", String(id));
  return updateDoc(ref, patch).then(() => {
    const cache = getCachedStudents().map(s => (String(s.id) === String(id) ? { ...s, ...patch } : s));
    localStorage.setItem("students_fs_cache", JSON.stringify(cache));
    const by = patch.updatedBy || "system";
    const at = new Date().toISOString();
    addDoc(collection(db, "audit_log"), { at, action: "update", studentId: String(id), by, campus: patch.campus || "" }).catch(() => {});
    return cache.find(s => String(s.id) === String(id)) || null;
  }).catch((err) => {
    emitFirestoreError({
      code: err?.code,
      message: err?.message,
      operation: "Update student",
      retry: () => updateDoc(ref, patch)
    });
    throw err;
  });
}

export async function refreshStudentsFromFirestore(scope = {}) {
  if (USE_LOCAL_FALLBACK) return getCachedStudents();
  let qref = collection(db, "students");
  const campus = String(scope.campus || "").toLowerCase();
  const isAdmin = Boolean(scope.isAdmin || scope.admin || scope.superadmin);
  if (campus && !isAdmin) {
    qref = query(qref, where("campus", "==", campus));
  }
  const snap = await getDocs(qref);
  const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  localStorage.setItem("students_fs_cache", JSON.stringify(list));
  return list;
}

export function deleteStudent(id) {
  const idStr = String(id);
  const removeFromLocal = () => {
    try {
      const list = getStoredStudents().filter(s => String(s.id) !== idStr);
      saveStoredStudents(list);
      // Also purge any cached Firestore list even when in local fallback
      const cacheRaw = localStorage.getItem("students_fs_cache");
      if (cacheRaw) {
        const cache = JSON.parse(cacheRaw).filter(s => String(s.id) !== idStr);
        localStorage.setItem("students_fs_cache", JSON.stringify(cache));
      }
    } catch {
      /* ignore */
    }
  };
  if (USE_LOCAL_FALLBACK) {
    removeFromLocal();
    return Promise.resolve(true);
  }
  const ref = doc(db, "students", idStr);
  return deleteDoc(ref).then(() => {
    removeFromLocal();
    const cache = getCachedStudents().filter(s => String(s.id) !== idStr);
    localStorage.setItem("students_fs_cache", JSON.stringify(cache));
    const at = new Date().toISOString();
    addDoc(collection(db, "audit_log"), { at, action: "delete", studentId: idStr, by: "system" }).catch(() => {});
    return true;
  }).catch((err) => {
    emitFirestoreError({
      code: err?.code,
      message: err?.message,
      operation: "Delete student",
      retry: () => deleteDoc(ref)
    });
    throw err;
  });
}

export async function uploadStudentPhoto(studentId, file) {
  const idStr = String(studentId);
  const fileRef = ref(storage, `students/${idStr}/profile.jpg`);
  const snap = await uploadBytes(fileRef, file);
  const url = await getDownloadURL(snap.ref);
  await updateStudent(idStr, { photoURL: url, photoUpdatedAt: new Date().toISOString() });
  return url;
}
